import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import './TestLoader.css';

const steps = [
    "Generating Questions",
    "Setting Difficulty",
    "Adapting to Your Class Level",
    "Creating Personalized Test"
];

const floatingIcons = [
    { icon: '∑', top: '10%', left: '15%', delay: '0s' },
    { icon: '🧪', top: '20%', left: '80%', delay: '2s' },
    { icon: '⚛', top: '70%', left: '10%', delay: '4s' },
    { icon: '📜', top: '80%', left: '85%', delay: '1s' },
    { icon: 'A', top: '15%', left: '50%', delay: '3s' },
    { icon: 'π', top: '60%', left: '25%', delay: '5s' },
    { icon: '🧬', top: '40%', left: '90%', delay: '2.5s' },
];

// SAMPLE_TEXT has been moved to AI generation in generate_test.php

const TestLoader = () => {
    const { t } = useTranslation(['loader', 'common']);
    const navigate = useNavigate();

    const location = useLocation();
    const meta = location.state || {};

    const board = meta.board || 'CBSE';
    const cls = meta.class || '10';
    const subject = meta.subject || 'Science';
    const subjectId = meta.subject_id || 1;
    const chapter = meta.chapter || 'Chapter 1';

    const [progress, setProgress] = useState(0);
    const [currentStep, setCurrentStep] = useState(0);
    const [stage, setStage] = useState('connecting'); // 'connecting', 'generating', 'finalizing'
    const [error, setError] = useState(null);

    // Derived status text
    const statusText = useMemo(() => {
        switch (stage) {
            case 'connecting': return t('loader:loader.connecting', 'Connecting to AI...');
            case 'generating': return t('loader:loader.generating', 'AI is generating questions...');
            case 'finalizing': return t('loader:loader.finalizing', 'Questions ready! Finalizing...');
            default: return '';
        }
    }, [stage, t]);


    // Refs to coordinate animation + API without closure stale-state issues
    const apiDoneRef = useRef(false);
    const questionsRef = useRef(null);
    const navigationFiredRef = useRef(false);
    const apiCalledRef = useRef(false);

    const doNavigate = (questions) => {
        if (navigationFiredRef.current) return;
        navigationFiredRef.current = true;
        navigate('/test', {
            state: {
                questions,
                board,
                class: cls,
                subject,
                subject_id: subjectId,
                chapter,
                chapter_id: meta.chapter_id || null
            }
        });
    };
    const steps = useMemo(() => [
        t('loader:loader.steps.gen', 'Generating Questions'),
        t('loader:loader.steps.diff', 'Setting Difficulty'),
        t('loader:loader.steps.adapt', 'Adapting to Your Class Level'),
        t('loader:loader.steps.create', 'Creating Personalized Test')
    ], [t]);

    // ── 1. Progress bar animation (interval-based, no rAF race) ──────────────
    useEffect(() => {
        let prog = 0;
        const interval = setInterval(() => {
            // After API is done, rush to 100; otherwise crawl to ~90
            const maxTarget = apiDoneRef.current ? 100 : 88;
            const increment = apiDoneRef.current ? 2.5 : 0.35;

            if (prog < maxTarget) {
                prog = Math.min(prog + increment, maxTarget);
                setProgress(prog);
                const stepIdx = Math.min(
                    Math.floor((prog / 100) * steps.length),
                    steps.length - 1
                );

                setCurrentStep(stepIdx);
            }

            // Both done → navigate
            if (prog >= 100 && apiDoneRef.current && questionsRef.current) {
                clearInterval(interval);
                setTimeout(() => doNavigate(questionsRef.current), 500);
            }
        }, 50);

        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── 2. API call ──────────────────────────────────────────────────────────
    useEffect(() => {
        if (apiCalledRef.current) return;   // prevent double-fire in StrictMode
        apiCalledRef.current = true;

        const board = meta.board || 'CBSE';
        const cls = meta.class || '10';
        const subject = meta.subject || 'Science';
        const subjectId = meta.subject_id || 1;
        const chapter = meta.chapter || 'Chapter 1';
        const chapterId = meta.chapter_id || null;

        const generateTest = async () => {
            try {
                setStage('generating');


                const response = await fetch(`${API_BASE_URL}generate_test.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({
                        class: cls,
                        subject: subject,
                        board: board,
                        chapter: chapter,
                        chapter_id: chapterId,
                        num_questions: 10
                    })
                });

                if (!response.ok) {
                    throw new Error(`Server returned HTTP ${response.status}`);
                }

                const data = await response.json();

                if (data.status !== 'success' || !Array.isArray(data.questions) || data.questions.length === 0) {
                    throw new Error(data.message || 'Invalid questions received from AI');
                }

                setStage('finalizing');
                questionsRef.current = data.questions;
                apiDoneRef.current = true;
                // If animation already finished, navigate immediately
                if (navigationFiredRef.current === false) {
                    setProgress(prev => {
                        if (prev >= 100) {
                            setTimeout(() => doNavigate(data.questions), 500);
                        }
                        return prev;
                    });
                }

            } catch (err) {
                console.error('Test generation error:', err);
                setError(err.message || 'Failed to generate test. Check your API key.');
            }
        };

        generateTest();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="test-loader-container">
            {/* Background Animated Glows */}
            <div className="background-glows">
                <div className="glow-1"></div>
                <div className="glow-2"></div>
            </div>

            {/* Floating Elements */}
            <div className="floating-elements">
                {floatingIcons.map((item, index) => (
                    <div
                        key={index}
                        className="floating-icon"
                        style={{ top: item.top, left: item.left, animationDelay: item.delay }}
                    >
                        {item.icon}
                    </div>
                ))}
            </div>

            {/* Header */}
            <div className="loader-header">
                <h1>{t('loader:loader.preparing', 'Preparing Your AI Diagnostic Test...')}</h1>
                <p>{board} &nbsp;·&nbsp; {t('loader:loader.classLabel', { cls, defaultValue: `Class ${cls}` })} &nbsp;·&nbsp; {subject} &nbsp;·&nbsp; {chapter}</p>
            </div>


            {/* Central Visual */}
            <div className="visual-container">
                <div className="energy-ring"></div>
                <div className="hologram-wrapper">
                    <img src="/ai_hologram.png" alt="AI Diagnostic Hologram" className="hologram-img" />
                </div>
            </div>

            {/* Error state */}
            {error ? (
                <div style={{
                    margin: '1rem auto', maxWidth: '480px', padding: '1rem 1.5rem',
                    background: 'rgba(255,50,50,0.12)', border: '1px solid rgba(255,80,80,0.4)',
                    borderRadius: '12px', color: '#ff6b6b', textAlign: 'center', fontSize: '0.9rem'
                }}>
                    ⚠️ {error}
                </div>
            ) : (
                <div className="progress-section">
                    <div className="progress-bar-container">
                        <div className="progress-bar-fill" style={{ width: `${progress}%` }}></div>
                    </div>
                    <p style={{ textAlign: 'center', opacity: 0.6, fontSize: '0.85rem', marginTop: '0.5rem' }}>
                        {statusText}
                    </p>
                    <div className="loading-steps">
                        {steps.map((step, index) => (
                            <div key={index} className={`step ${index <= currentStep ? 'active' : ''}`}>
                                {step}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Footer */}
            <div className="loader-footer">
                <p>
                    {t('loader:loader.takeSeconds', 'This will take just a few seconds')}

                    <span className="dot-animation">
                        <span className="dot"></span>
                        <span className="dot"></span>
                        <span className="dot"></span>
                    </span>
                </p>
            </div>
        </div>
    );
};

export default TestLoader;
